import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { Image as ImageIcon, Search, RotateCcw, Maximize2, Camera } from 'lucide-react';
import JubileeImageLightbox from '../viewers/JubileeImageLightbox';
import { jubileeStorageService } from '../services/jubileeStorageService';

export default function JubileeImageGallery({ initialFilter = {} }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(initialFilter.year || 'all');
  const [selectedEvent, setSelectedEvent] = useState(initialFilter.eventId || 'all');
  const [selectedCategory, setSelectedCategory] = useState(initialFilter.categoryId || 'all');

  const allImages = useMemo(() => jubileeStorageService.getMediaByType('image'), []);
  const events = useMemo(() => jubileeStorageService.getEvents(), []);
  const categories = useMemo(() => jubileeStorageService.getCategories(), []);

  const filteredImages = useMemo(() => {
    return jubileeStorageService.searchMedia({
      mediaType: 'image',
      query: searchQuery,
      year: selectedYear,
      eventId: selectedEvent,
      categoryId: selectedCategory,
    });
  }, [searchQuery, selectedYear, selectedEvent, selectedCategory]);

  const handleOpenImage = (idx) => {
    setSelectedIndex(idx);
    setShowLightbox(true);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedYear('all');
    setSelectedEvent('all');
    setSelectedCategory('all');
  };

  const hasActiveFilters = searchQuery !== '' || selectedYear !== 'all' || selectedEvent !== 'all' || selectedCategory !== 'all';
  const availableYears = Array.from(new Set(allImages.map((img) => img.year))).sort((a, b) => b - a);

  return (
    <div className="py-4">
      <Container>
        {/* Gallery Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 pb-3 border-bottom">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="p-2 rounded-3 text-white d-inline-flex" style={{ background: '#d97706' }}>
                <ImageIcon size={22} />
              </div>
              <h2 className="fw-bold m-0 text-slate-900" style={{ letterSpacing: '-0.5px' }}>
                Historical Photograph Gallery
              </h2>
            </div>
            <p className="text-muted small m-0">
              Explore vintage glass plates, celebratory milestones, archival portraits, and palm leaf preservation.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span
              className="badge px-3 py-2 rounded-pill fw-semibold"
              style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}
            >
              {filteredImages.length} {filteredImages.length === 1 ? 'Photograph' : 'Photographs'} Available
            </span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white p-3 rounded-4 shadow-sm border mb-4">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={4}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light border-end-0">
                  <Search size={15} className="text-muted" />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search photographs by caption, event, photographer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-start-0"
                />
              </div>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                <option value="all">All Events</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">All Years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    Year {y}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2} className="text-end">
              {hasActiveFilters && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                  className="w-100 d-flex align-items-center justify-content-center gap-1"
                >
                  <RotateCcw size={13} /> Clear Filters
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {/* Image Masonry / Grid */}
        {filteredImages.length > 0 ? (
          <Row className="g-4">
            {filteredImages.map((image, idx) => (
              <Col key={image.id} xs={12} sm={6} md={4}>
                <div
                  className="jubilee-media-card cursor-pointer"
                  onClick={() => handleOpenImage(idx)}
                >
                  <div className="media-thumbnail-wrapper" style={{ height: '240px' }}>
                    <img
                      src={image.thumbnailUrl || image.fileUrl}
                      alt={image.title}
                      className="media-thumbnail-img"
                      loading="lazy"
                    />
                    <span className="media-type-badge image">
                      <ImageIcon size={12} /> Photo
                    </span>
                    <div className="media-play-overlay">
                      <div className="play-circle-btn">
                        <Maximize2 size={20} color="#0f172a" />
                      </div>
                    </div>
                  </div>

                  <div className="media-card-body">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="badge bg-warning bg-opacity-25 text-dark border border-warning border-opacity-50" style={{ fontSize: '0.72rem' }}>
                        {image.year}
                      </span>
                      {image.categoryName && (
                        <span className="badge bg-light text-secondary border text-truncate" style={{ fontSize: '0.72rem', maxWidth: '140px' }}>
                          {image.categoryName}
                        </span>
                      )}
                    </div>

                    <h5 className="media-card-title">{image.title}</h5>
                    <p className="media-card-desc">{image.description}</p>

                    {image.author && (
                      <div className="small text-muted mb-2 d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                        <Camera size={13} className="text-warning" />
                        <span className="text-truncate">Photo by: {image.author}</span>
                      </div>
                    )}

                    <div className="media-card-meta">
                      <span className="text-truncate" style={{ maxWidth: '180px' }}>
                        {image.eventName || 'Jubilee Collection'}
                      </span>
                      <span className="fw-semibold text-warning small">Enlarge Photo →</span>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-center py-5 bg-white rounded-4 border my-4">
            <div className="p-3 rounded-circle d-inline-flex mb-3" style={{ background: '#fef3c7', color: '#b45309' }}>
              <ImageIcon size={36} />
            </div>
            <h5 className="fw-bold text-dark">No photographs match this filter</h5>
            <p className="text-muted small mb-3">
              Try adjusting your search keywords or clearing active filters.
            </p>
            {hasActiveFilters && (
              <Button variant="outline-warning" size="sm" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </Container>

      {/* Lightbox Modal */}
      <JubileeImageLightbox
        images={filteredImages}
        selectedIndex={selectedIndex}
        show={showLightbox}
        onHide={() => setShowLightbox(false)}
        onSelectIndex={(newIdx) => setSelectedIndex(newIdx)}
      />
    </div>
  );
}
